import { createLdsMemoryCache, createLdsRequestCache } from "../../components/lds/cache";
import { getClassroom, getSchoolYear, getTeacher, listClassrooms, listSchoolYears, listTeachers } from "./api";
import type { Classroom, EntityId, SchoolYear, Teacher } from "./types";

const STALE_MS = 2 * 60 * 1000;

interface Timed<T> {
  value: T;
  loadedAt: number;
}

const now = () => Date.now();
const isFresh = <T,>(entry: Timed<T> | null) => Boolean(entry && now() - entry.loadedAt < STALE_MS);

const teachersCache = createLdsMemoryCache<Timed<Teacher[]> | null>(null);
const yearsCache = createLdsMemoryCache<Timed<SchoolYear[]> | null>(null);
const classroomsCache = createLdsMemoryCache<Timed<Classroom[]> | null>(null);

const teacherDetails = new Map<string, Timed<Teacher>>();
const yearDetails = new Map<string, Timed<SchoolYear>>();
const classroomDetails = new Map<string, Timed<Classroom>>();

const loadTeachersOnce = createLdsRequestCache<Teacher[]>();
const loadYearsOnce = createLdsRequestCache<SchoolYear[]>();
const loadClassroomsOnce = createLdsRequestCache<Classroom[]>();
const detailRequests = new Map<string, Promise<unknown>>();

const keyOf = (id: EntityId) => String(id);
const withTimed = <T,>(value: T): Timed<T> => ({ value, loadedAt: now() });

export const hasTeacherListCache = () => teachersCache.has();
export const getCachedTeachers = () => teachersCache.get()?.value ?? null;
export const loadTeacherList = async (force = false): Promise<Teacher[]> => {
  const cached = teachersCache.get();
  if (!force && isFresh(cached)) return cached?.value ?? [];
  const data = await loadTeachersOnce(listTeachers);
  teachersCache.set(withTimed(data));
  data.forEach((teacher) => teacherDetails.set(keyOf(teacher.id), withTimed(teacher)));
  return data;
};

export const upsertCachedTeacher = (teacher: Teacher) => {
  teacherDetails.set(keyOf(teacher.id), withTimed(teacher));
  teachersCache.update((entry) =>
    entry
      ? withTimed([
          teacher,
          ...entry.value.filter((item) => String(item.id) !== String(teacher.id)),
        ])
      : entry
  );
};

export const removeCachedTeacher = (id: EntityId) => {
  teacherDetails.delete(keyOf(id));
  teachersCache.update((entry) => (entry ? withTimed(entry.value.filter((item) => String(item.id) !== String(id))) : entry));
};

export const loadTeacherDetails = async (id: EntityId, force = false): Promise<Teacher> => {
  const cacheKey = `teacher:${keyOf(id)}`;
  const cached = teacherDetails.get(keyOf(id)) ?? null;
  if (!force && isFresh(cached)) return cached.value;
  const existing = detailRequests.get(cacheKey) as Promise<Teacher> | undefined;
  if (existing) return existing;
  const request = getTeacher(id).then((teacher) => {
    upsertCachedTeacher(teacher);
    return teacher;
  }).finally(() => detailRequests.delete(cacheKey));
  detailRequests.set(cacheKey, request);
  return request;
};

export const hasSchoolYearListCache = () => yearsCache.has();
export const getCachedSchoolYears = () => yearsCache.get()?.value ?? null;
export const loadSchoolYearList = async (force = false): Promise<SchoolYear[]> => {
  const cached = yearsCache.get();
  if (!force && isFresh(cached)) return cached?.value ?? [];
  const data = await loadYearsOnce(listSchoolYears);
  yearsCache.set(withTimed(data));
  data.forEach((year) => yearDetails.set(keyOf(year.id), withTimed(year)));
  return data;
};

export const upsertCachedSchoolYear = (year: SchoolYear) => {
  yearDetails.set(keyOf(year.id), withTimed(year));
  yearsCache.update((entry) =>
    entry ? withTimed([year, ...entry.value.filter((item) => String(item.id) !== String(year.id))]) : entry
  );
};

export const removeCachedSchoolYear = (id: EntityId) => {
  yearDetails.delete(keyOf(id));
  yearsCache.update((entry) => (entry ? withTimed(entry.value.filter((item) => String(item.id) !== String(id))) : entry));
};

export const loadSchoolYearDetails = async (id: EntityId, force = false): Promise<SchoolYear> => {
  const cacheKey = `year:${keyOf(id)}`;
  const cached = yearDetails.get(keyOf(id)) ?? null;
  if (!force && isFresh(cached)) return cached.value;
  const existing = detailRequests.get(cacheKey) as Promise<SchoolYear> | undefined;
  if (existing) return existing;
  const request = getSchoolYear(id).then((year) => {
    upsertCachedSchoolYear(year);
    return year;
  }).finally(() => detailRequests.delete(cacheKey));
  detailRequests.set(cacheKey, request);
  return request;
};

export const hasClassroomListCache = () => classroomsCache.has();
export const getCachedClassrooms = () => classroomsCache.get()?.value ?? null;
export const loadClassroomList = async (force = false): Promise<Classroom[]> => {
  const cached = classroomsCache.get();
  if (!force && isFresh(cached)) return cached?.value ?? [];
  const data = await loadClassroomsOnce(listClassrooms);
  classroomsCache.set(withTimed(data));
  data.forEach((classroom) => classroomDetails.set(keyOf(classroom.id), withTimed(classroom)));
  return data;
};

export const upsertCachedClassroom = (classroom: Classroom) => {
  classroomDetails.set(keyOf(classroom.id), withTimed(classroom));
  classroomsCache.update((entry) =>
    entry
      ? withTimed([classroom, ...entry.value.filter((item) => String(item.id) !== String(classroom.id))])
      : entry
  );
};

export const addCachedClassrooms = (classrooms: Classroom[]) => {
  classrooms.forEach((classroom) => classroomDetails.set(keyOf(classroom.id), withTimed(classroom)));
  classroomsCache.update((entry) => (entry ? withTimed([...classrooms, ...entry.value]) : entry));
};

export const removeCachedClassroom = (id: EntityId) => {
  classroomDetails.delete(keyOf(id));
  classroomsCache.update((entry) => (entry ? withTimed(entry.value.filter((item) => String(item.id) !== String(id))) : entry));
};

export const loadClassroomDetails = async (id: EntityId, force = false): Promise<Classroom> => {
  const cacheKey = `classroom:${keyOf(id)}`;
  const cached = classroomDetails.get(keyOf(id)) ?? null;
  if (!force && isFresh(cached)) return cached.value;
  const existing = detailRequests.get(cacheKey) as Promise<Classroom> | undefined;
  if (existing) return existing;
  const request = getClassroom(id).then((classroom) => {
    upsertCachedClassroom(classroom);
    return classroom;
  }).finally(() => detailRequests.delete(cacheKey));
  detailRequests.set(cacheKey, request);
  return request;
};
